// src/components/WizardLancamento.js
//
// Wizard guiado para lançamento rápido de contas a pagar e a receber.
// Uso:
//   <WizardLancamento mode="pagar"   onClose={() => {}} onSave={reload} />
//   <WizardLancamento mode="receber" onClose={() => {}} onSave={reload} />
//
// Arquitetura:
//   - Steps definidos como dados (STEPS_PAGAR / STEPS_RECEBER)
//   - Um único renderizador genérico por tipo de step
//   - Nenhuma dependência de CSS externo — tudo inline + <style> injetado

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  pagar: {
    accent:     '#0d7377',
    accentSoft: '#e8f4f5',
    accentText: '#095e62',
    gradient:   'linear-gradient(135deg, #0d7377 0%, #14a085 100%)',
    emoji:      '💸',
    title:      'Lançar Conta a Pagar',
    sub:        'Vamos registrar juntos em 4 passos simples.',
    api:        '/api/contas-a-pagar/manual',
    successMsg: 'Conta a pagar criada com sucesso!',
  },
  receber: {
    accent:     '#276749',
    accentSoft: '#f0fdf6',
    accentText: '#1a4f36',
    gradient:   'linear-gradient(135deg, #276749 0%, #3a856a 100%)',
    emoji:      '💰',
    title:      'Lançar Conta a Receber',
    sub:        'Registre um recebimento em poucos cliques.',
    api:        '/api/contas-a-receber',
    successMsg: 'Conta a receber criada com sucesso!',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtCurrency = (v) =>
  v ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '—';

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const addDays = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const fmtDate = (s) => {
  if (!s) return '—';
  const [y,m,d] = s.split('-');
  return `${d}/${m}/${y}`;
};

// ─── Step configs ─────────────────────────────────────────────────────────────

const CATEGORIAS_PAGAR = [
  { key: 'fornecedor',    label: 'Fornecedor',    emoji: '🏭', hint: 'Pagamento a fornecedor ou prestador' },
  { key: 'frota',         label: 'Frota',         emoji: '🚗', hint: 'Combustível, manutenção, pedágio' },
  { key: 'pessoal',       label: 'Pessoal',       emoji: '👤', hint: 'Salário, autônomo, RPA' },
  { key: 'impostos',      label: 'Impostos',      emoji: '📋', hint: 'Taxas, tributos, guias' },
  { key: 'aluguel',       label: 'Aluguel',       emoji: '🏢', hint: 'Imóvel, equipamento, software' },
  { key: 'outros',        label: 'Outro gasto',   emoji: '📦', hint: 'Qualquer outro tipo' },
];

const CATEGORIAS_RECEBER = [
  { key: 'servico',      label: 'Serviço prestado', emoji: '🔧', hint: 'Pagamento por trabalho feito' },
  { key: 'venda',        label: 'Venda',            emoji: '🛒', hint: 'Produto ou mercadoria vendida' },
  { key: 'cliente',      label: 'Cliente avulso',   emoji: '👤', hint: 'Pagamento de cliente' },
  { key: 'mensalidade',  label: 'Mensalidade',      emoji: '🔁', hint: 'Contrato ou assinatura recorrente' },
  { key: 'reembolso',    label: 'Reembolso',        emoji: '↩️', hint: 'Devolução ou estorno' },
  { key: 'outros',       label: 'Outra receita',    emoji: '💼', hint: 'Qualquer outra entrada' },
];

const QUANDO_PAGAR = [
  { key: 'hoje',    label: 'Hoje',          emoji: '⚡', fn: () => todayISO() },
  { key: '7',       label: 'Em 7 dias',      emoji: '📅', fn: () => addDays(7)  },
  { key: '15',      label: 'Em 15 dias',     emoji: '📅', fn: () => addDays(15) },
  { key: '30',      label: 'Em 30 dias',     emoji: '📅', fn: () => addDays(30) },
  { key: 'custom',  label: 'Outra data',     emoji: '🗓️', fn: null },
];

const QUANDO_RECEBER = [
  { key: 'hoje',    label: 'Hoje',          emoji: '⚡', fn: () => todayISO() },
  { key: '3',       label: 'Em 3 dias',      emoji: '📅', fn: () => addDays(3)  },
  { key: '7',       label: 'Em 7 dias',      emoji: '📅', fn: () => addDays(7)  },
  { key: '30',      label: 'Em 30 dias',     emoji: '📅', fn: () => addDays(30) },
  { key: 'custom',  label: 'Outra data',     emoji: '🗓️', fn: null },
];

// Sugestões de descrição por categoria
const SUGESTOES = {
  fornecedor:   ['Pagamento de nota fiscal', 'Compra de material', 'Serviço contratado'],
  frota:        ['Abastecimento veículo', 'Manutenção preventiva', 'Pagamento de pedágio'],
  pessoal:      ['Salário do mês', 'Pagamento autônomo', 'Remuneração serviço'],
  impostos:     ['Guia de imposto', 'Taxa municipal', 'INSS / FGTS'],
  aluguel:      ['Aluguel mensal', 'Mensalidade software', 'Locação equipamento'],
  servico:      ['Serviço executado', 'Mão de obra', 'Consultoria'],
  venda:        ['Venda de produto', 'Entrega realizada', 'Faturamento pedido'],
  cliente:      ['Pagamento de cliente', 'Recebimento avulso'],
  mensalidade:  ['Mensalidade contrato', 'Assinatura recorrente'],
  reembolso:    ['Reembolso despesa', 'Estorno de cobrança'],
  outros:       [],
};

// ─── CSS injetado ──────────────────────────────────────────────────────────────
const injectCSS = () => {
  if (document.getElementById('wiz-styles')) return;
  const s = document.createElement('style');
  s.id = 'wiz-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');

    .wiz-overlay {
      position: fixed; inset: 0; z-index: 99999;
      background: rgba(10,14,30,0.55);
      backdrop-filter: blur(6px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
      animation: wizFadeIn .2s ease;
    }
    @keyframes wizFadeIn { from { opacity:0 } to { opacity:1 } }

    .wiz-card {
      font-family: 'Sora', system-ui, sans-serif;
      background: #fff;
      border-radius: 24px;
      box-shadow: 0 32px 80px rgba(10,14,30,0.22), 0 0 0 1px rgba(0,0,0,0.04);
      width: 100%; max-width: 520px;
      max-height: 90vh;
      overflow: hidden;
      display: flex; flex-direction: column;
      animation: wizSlideUp .32s cubic-bezier(0.16,1,0.3,1);
    }
    @keyframes wizSlideUp {
      from { opacity:0; transform: translateY(28px) scale(0.97) }
      to   { opacity:1; transform: translateY(0)    scale(1)    }
    }

    /* step transition */
    .wiz-step-wrap {
      overflow: hidden;
      flex: 1;
    }
    .wiz-step-inner {
      padding: 28px 32px 24px;
      animation: wizStepIn .25s cubic-bezier(0.16,1,0.3,1);
    }
    @keyframes wizStepIn {
      from { opacity:0; transform: translateX(18px) }
      to   { opacity:1; transform: translateX(0) }
    }
    .wiz-step-inner.back {
      animation: wizStepInBack .25s cubic-bezier(0.16,1,0.3,1);
    }
    @keyframes wizStepInBack {
      from { opacity:0; transform: translateX(-18px) }
      to   { opacity:1; transform: translateX(0) }
    }

    /* progress bar */
    .wiz-progress {
      height: 3px; background: #f1f5f9; border-radius: 0;
      overflow: hidden;
    }
    .wiz-progress-fill {
      height: 100%; border-radius: 0;
      transition: width .4s cubic-bezier(0.16,1,0.3,1);
    }

    /* step label */
    .wiz-step-label {
      font-size: 0.7rem; font-weight: 700; letter-spacing: .8px;
      text-transform: uppercase; opacity: .45;
      margin-bottom: 6px;
    }
    .wiz-step-title {
      font-size: 1.3rem; font-weight: 800;
      color: #0f172a; line-height: 1.2;
      margin: 0 0 6px;
      letter-spacing: -0.3px;
    }
    .wiz-step-sub {
      font-size: 0.82rem; color: #64748b;
      margin: 0 0 22px; line-height: 1.5;
    }

    /* category grid */
    .wiz-cat-grid {
      display: grid; grid-template-columns: 1fr 1fr;
      gap: 8px;
    }
    .wiz-cat-btn {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 12px 14px;
      border-radius: 12px; border: 1.5px solid #e2e8f0;
      background: #f8fafc; cursor: pointer;
      transition: all .15s; text-align: left;
      font-family: 'Sora', sans-serif;
    }
    .wiz-cat-btn:hover { border-color: var(--wiz-accent); background: var(--wiz-soft); }
    .wiz-cat-btn.selected {
      border-color: var(--wiz-accent);
      background: var(--wiz-soft);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--wiz-accent) 15%, transparent);
    }
    .wiz-cat-emoji {
      font-size: 1.4rem; flex-shrink: 0; line-height: 1;
      margin-top: 2px;
    }
    .wiz-cat-label {
      display: block;
      font-size: 0.82rem; font-weight: 700; color: #1e293b;
      line-height: 1.2; margin-bottom: 3px;
    }
    .wiz-cat-hint {
      display: block;
      font-size: 0.68rem; color: #94a3b8; line-height: 1.3;
    }

    /* valor input — prefixo em compartimento próprio */
    .wiz-valor-wrap {
      display: flex; align-items: stretch;
      margin-bottom: 10px;
      border: 2px solid #e2e8f0; border-radius: 14px;
      background: #f8fafc; overflow: hidden;
      transition: border-color .15s, box-shadow .15s;
    }
    .wiz-valor-wrap:focus-within {
      border-color: var(--wiz-accent);
      background: #fff;
      box-shadow: 0 0 0 4px color-mix(in srgb, var(--wiz-accent) 12%, transparent);
    }
    .wiz-valor-prefix {
      display: flex; align-items: center;
      padding: 0 14px 0 18px;
      font-size: 1rem; font-weight: 700; color: #94a3b8;
      background: #f1f5f9;
      border-right: 1.5px solid #e2e8f0;
      flex-shrink: 0; user-select: none;
    }
    .wiz-valor-input {
      flex: 1; min-width: 0;
      padding: 18px 16px;
      font-family: 'Sora', sans-serif;
      font-size: 2rem; font-weight: 800; color: #0f172a;
      border: none; background: transparent;
      outline: none; letter-spacing: -1px;
    }

    /* quando pills */
    .wiz-quando-list {
      display: flex; flex-direction: column; gap: 6px;
    }
    .wiz-quando-btn {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 16px;
      border-radius: 12px; border: 1.5px solid #e2e8f0;
      background: #f8fafc; cursor: pointer;
      font-family: 'Sora', sans-serif;
      font-size: 0.88rem; font-weight: 600; color: #334155;
      transition: all .15s;
    }
    .wiz-quando-btn:hover  { border-color: var(--wiz-accent); background: var(--wiz-soft); }
    .wiz-quando-btn.selected {
      border-color: var(--wiz-accent); background: var(--wiz-soft);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--wiz-accent) 12%, transparent);
    }
    .wiz-quando-emoji { font-size: 1rem; width: 20px; text-align: center; flex-shrink: 0; }
    .wiz-date-input {
      width: 100%; margin-top: 8px;
      padding: 10px 14px; font-family: 'Sora', sans-serif;
      font-size: 0.9rem; color: #0f172a;
      border: 1.5px solid var(--wiz-accent); border-radius: 10px;
      background: var(--wiz-soft); outline: none;
      box-sizing: border-box;
    }

    /* descricao */
    .wiz-desc-input {
      width: 100%; padding: 14px;
      font-family: 'Sora', sans-serif; font-size: 0.95rem; color: #0f172a;
      border: 1.5px solid #e2e8f0; border-radius: 12px;
      background: #f8fafc; outline: none; box-sizing: border-box;
      margin-bottom: 10px;
      transition: border-color .15s, box-shadow .15s;
    }
    .wiz-desc-input:focus {
      border-color: var(--wiz-accent); background: #fff;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--wiz-accent) 12%, transparent);
    }
    .wiz-sugestoes {
      display: flex; flex-wrap: wrap; gap: 6px;
    }
    .wiz-sug-pill {
      padding: 4px 10px; border-radius: 999px;
      background: #f1f5f9; border: 1px solid #e2e8f0;
      font-size: 0.72rem; font-weight: 600; color: #475569;
      cursor: pointer; transition: all .12s;
      font-family: 'Sora', sans-serif;
    }
    .wiz-sug-pill:hover { background: var(--wiz-soft); border-color: var(--wiz-accent); color: var(--wiz-text); }

    /* cliente input */
    .wiz-cliente-input {
      width: 100%; padding: 14px;
      font-family: 'Sora', sans-serif; font-size: 0.95rem; color: #0f172a;
      border: 1.5px solid #e2e8f0; border-radius: 12px;
      background: #f8fafc; outline: none; box-sizing: border-box;
      transition: border-color .15s;
    }
    .wiz-cliente-input:focus { border-color: var(--wiz-accent); background: #fff; }

    /* confirmação */
    .wiz-confirm-card {
      background: var(--wiz-soft);
      border: 1.5px solid color-mix(in srgb, var(--wiz-accent) 25%, transparent);
      border-radius: 16px; padding: 20px;
      margin-bottom: 6px;
    }
    .wiz-confirm-row {
      display: flex; align-items: center; justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid color-mix(in srgb, var(--wiz-accent) 15%, transparent);
    }
    .wiz-confirm-row:last-child { border-bottom: none; padding-bottom: 0; }
    .wiz-confirm-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #64748b; }
    .wiz-confirm-val   { font-size: 0.9rem; font-weight: 700; color: #0f172a; text-align: right; max-width: 60%; }
    .wiz-confirm-valor {
      font-size: 2rem; font-weight: 800; letter-spacing: -1px;
      color: var(--wiz-accent); text-align: center; margin: 8px 0 4px;
    }

    /* footer */
    .wiz-footer {
      display: flex; align-items: center; gap: 10px;
      padding: 16px 32px 24px;
      border-top: 1px solid #f1f5f9;
    }
    .wiz-btn-back {
      flex-shrink: 0;
      width: 40px; height: 40px; border-radius: 10px;
      border: 1.5px solid #e2e8f0; background: #f8fafc;
      font-size: 1rem; cursor: pointer; color: #64748b;
      display: flex; align-items: center; justify-content: center;
      transition: all .15s; font-family: 'Sora', sans-serif;
    }
    .wiz-btn-back:hover { border-color: #94a3b8; background: #f1f5f9; color: #334155; }
    .wiz-btn-next {
      flex: 1; padding: 14px;
      border: none; border-radius: 12px;
      font-family: 'Sora', sans-serif; font-size: 0.95rem; font-weight: 700;
      color: #fff; cursor: pointer;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      transition: all .18s;
      letter-spacing: 0.01em;
    }
    .wiz-btn-next:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0,0,0,0.18); }
    .wiz-btn-next:disabled { opacity: .4; cursor: not-allowed; }

    /* success */
    .wiz-success {
      padding: 48px 32px;
      display: flex; flex-direction: column; align-items: center; text-align: center;
      animation: wizSuccessIn .45s cubic-bezier(0.16,1,0.3,1);
    }
    @keyframes wizSuccessIn {
      from { opacity:0; transform: scale(0.92) }
      to   { opacity:1; transform: scale(1) }
    }
    .wiz-success-ring {
      width: 80px; height: 80px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 2.4rem; margin-bottom: 20px;
      animation: wizPulse 1.8s ease 0.2s infinite;
    }
    @keyframes wizPulse {
      0%  { box-shadow: 0 0 0 0   color-mix(in srgb, var(--wiz-accent) 35%, transparent) }
      60% { box-shadow: 0 0 0 18px transparent }
      100%{ box-shadow: 0 0 0 0   transparent }
    }
    .wiz-success-title { font-size: 1.5rem; font-weight: 800; color: #0f172a; margin: 0 0 8px; letter-spacing: -0.3px; }
    .wiz-success-sub   { font-size: 0.88rem; color: #64748b; margin: 0 0 28px; line-height: 1.5; }
    .wiz-success-close {
      padding: 12px 32px; border-radius: 12px; border: none;
      font-family: 'Sora', sans-serif; font-size: 0.9rem; font-weight: 700;
      color: #fff; cursor: pointer; transition: all .18s;
    }
    .wiz-success-close:hover { filter: brightness(1.08); transform: translateY(-1px); }

    /* skip link */
    .wiz-skip {
      display: block; text-align: center; margin-top: 10px;
      font-size: 0.75rem; color: #94a3b8; cursor: pointer;
      font-family: 'Sora', sans-serif;
      transition: color .15s;
    }
    .wiz-skip:hover { color: #64748b; }

    /* header */
    .wiz-header {
      padding: 22px 32px 0;
      display: flex; align-items: flex-start; justify-content: space-between;
    }
    .wiz-header-left {}
    .wiz-header-emoji { font-size: 1.8rem; margin-bottom: 4px; }
    .wiz-header-title { font-size: 1rem; font-weight: 800; color: #0f172a; margin: 0; letter-spacing: -0.2px; }
    .wiz-header-sub   { font-size: 0.72rem; color: #94a3b8; margin: 2px 0 0; }
    .wiz-close-btn {
      width: 32px; height: 32px; border-radius: 8px;
      border: 1px solid #e2e8f0; background: #f8fafc;
      font-size: 0.85rem; cursor: pointer; color: #94a3b8;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; transition: all .15s;
      font-family: 'Sora', sans-serif;
    }
    .wiz-close-btn:hover { background: #fee2e2; border-color: #fca5a5; color: #dc2626; }

    @media (max-width: 600px) {
      .wiz-card { border-radius: 20px 20px 0 0; max-height: 92vh; }
      .wiz-overlay { align-items: flex-end; padding: 0; }
      .wiz-cat-grid { grid-template-columns: 1fr; }
      .wiz-step-inner { padding: 22px 20px 18px; }
      .wiz-footer { padding: 14px 20px 20px; }
      .wiz-header { padding: 18px 20px 0; }
    }
  `;
  document.head.appendChild(s);
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function WizardLancamento({ mode = 'pagar', onClose, onSave }) {
  useEffect(() => { injectCSS(); }, []);

  const cfg = T[mode];
  const categorias = mode === 'pagar' ? CATEGORIAS_PAGAR : CATEGORIAS_RECEBER;
  const quandoOpts = mode === 'pagar' ? QUANDO_PAGAR    : QUANDO_RECEBER;

  // Steps: 0=categoria 1=valor 2=quando 3=descricao [4=cliente só se receber] 5=confirmação
  const totalSteps = mode === 'receber' ? 5 : 4; // 0-indexed confirm = totalSteps-1

  const [step, setStep]           = useState(0);
  const [dir,  setDir]            = useState('fwd');
  const [submitting, setSubmitting] = useState(false);
  const [success,    setSuccess]    = useState(false);

  // Form state
  const [categoria,    setCategoria]    = useState(null);
  const [valor,        setValor]        = useState('');
  const [quandoKey,    setQuandoKey]    = useState(null);
  const [dataCustom,   setDataCustom]   = useState('');
  const [descricao,    setDescricao]    = useState('');
  const [clienteNome,  setClienteNome]  = useState('');

  const inputRef = useRef(null);

  // CSS variable injection per instance
  const rootStyle = {
    '--wiz-accent': cfg.accent,
    '--wiz-soft':   cfg.accentSoft,
    '--wiz-text':   cfg.accentText,
  };

  // Focus valor input when step changes to 1
  useEffect(() => {
    if (step === 1 && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [step]);

  // Computed date from selection
  const dataVencimento = quandoKey === 'custom'
    ? dataCustom
    : quandoOpts.find(o => o.key === quandoKey)?.fn?.() ?? '';

  // Step validity
  const canAdvance = (() => {
    if (step === 0) return !!categoria;
    if (step === 1) return parseFloat(valor) > 0;
    if (step === 2) return quandoKey === 'custom' ? !!dataCustom : !!quandoKey;
    if (step === 3) return descricao.trim().length >= 3;
    if (step === 4 && mode === 'receber') return true; // cliente é opcional
    return true;
  })();

  const go = useCallback((n) => {
    setDir(n > 0 ? 'fwd' : 'bck');
    setStep(s => s + n);
  }, []);

  const sugestoes = categoria ? (SUGESTOES[categoria.key] || []) : [];
  const confirmStep = mode === 'receber' ? 4 : 3;

  // Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = mode === 'pagar'
        ? {
            descricao,
            valor,
            data_vencimento: dataVencimento,
          }
        : {
            descricao,
            valor,
            data_vencimento: dataVencimento,
            cliente_nome:    clienteNome || undefined,
          };

      await api.post(cfg.api, payload);
      setSuccess(true);
      onSave?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step label ──────────────────────────────────────────────────────────────
  const stepLabels = mode === 'pagar'
    ? ['Tipo', 'Valor', 'Quando', 'Descrição']
    : ['Tipo', 'Valor', 'Quando', 'Descrição', 'Cliente'];

  // ── Render steps ────────────────────────────────────────────────────────────
  const renderStep = () => {
    const cls = `wiz-step-inner${dir === 'bck' ? ' back' : ''}`;

    // Step 0 — Categoria
    if (step === 0) return (
      <div key="s0" className={cls}>
        <p className="wiz-step-label">Passo 1 de {stepLabels.length}</p>
        <h2 className="wiz-step-title">
          {mode === 'pagar' ? 'O que você precisa pagar?' : 'De onde vem esse dinheiro?'}
        </h2>
        <p className="wiz-step-sub">Escolha a categoria que melhor descreve.</p>
        <div className="wiz-cat-grid">
          {categorias.map(c => (
            <button
              key={c.key}
              className={`wiz-cat-btn ${categoria?.key === c.key ? 'selected' : ''}`}
              onClick={() => { 
                setCategoria(c); 
                setDescricao(''); 
                setTimeout(() => go(1), 150); 
              }}
            >
              <span className="wiz-cat-emoji">{c.emoji}</span>
              <span>
                <span className="wiz-cat-label">{c.label}</span>
                <span className="wiz-cat-hint">{c.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );

    // Step 1 — Valor
    if (step === 1) return (
      <div key="s1" className={cls}>
        <p className="wiz-step-label">Passo 2 de {stepLabels.length}</p>
        <h2 className="wiz-step-title">Qual é o valor?</h2>
        <p className="wiz-step-sub">
          {mode === 'pagar' ? 'Quanto você precisa pagar?' : 'Quanto você vai receber?'}
        </p>
        <div className="wiz-valor-wrap">
          <span className="wiz-valor-prefix">R$</span>
          <input
            ref={inputRef}
            className="wiz-valor-input"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            placeholder="0,00"
            value={valor}
            onChange={e => setValor(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && canAdvance && go(1)}
          />
        </div>
        {parseFloat(valor) > 0 && (
          <p style={{ textAlign: 'center', color: cfg.accentText, fontWeight: 700, fontSize: '0.9rem', marginTop: 4 }}>
            {fmtCurrency(parseFloat(valor))}
          </p>
        )}
      </div>
    );

    // Step 2 — Quando
    if (step === 2) return (
      <div key="s2" className={cls}>
        <p className="wiz-step-label">Passo 3 de {stepLabels.length}</p>
        <h2 className="wiz-step-title">
          {mode === 'pagar' ? 'Quando vence?' : 'Quando você recebe?'}
        </h2>
        <p className="wiz-step-sub">Selecione o prazo para este lançamento.</p>
        <div className="wiz-quando-list">
          {quandoOpts.map(o => (
            <button
              key={o.key}
              className={`wiz-quando-btn ${quandoKey === o.key ? 'selected' : ''}`}
              onClick={() => { 
                setQuandoKey(o.key); 
                if (o.key !== 'custom') {
                  setTimeout(() => go(1), 150); 
                }
              }}
            >
              <span className="wiz-quando-emoji">{o.emoji}</span>
              <span>{o.label}</span>
              {o.fn && <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>{fmtDate(o.fn())}</span>}
            </button>
          ))}
        </div>
        {quandoKey === 'custom' && (
          <input
            className="wiz-date-input"
            type="date"
            value={dataCustom}
            min={todayISO()}
            onChange={e => setDataCustom(e.target.value)}
            autoFocus
          />
        )}
      </div>
    );

    // Step 3 — Descrição
    if (step === 3) return (
      <div key="s3" className={cls}>
        <p className="wiz-step-label">Passo 4 de {stepLabels.length}</p>
        <h2 className="wiz-step-title">Como se chama esse {mode === 'pagar' ? 'gasto' : 'recebimento'}?</h2>
        <p className="wiz-step-sub">Uma descrição rápida para identificar depois.</p>
        <input
          className="wiz-desc-input"
          type="text"
          placeholder={mode === 'pagar' ? 'Ex: Pagamento fornecedor mês atual' : 'Ex: Serviço de guincho cliente João'}
          value={descricao}
          onChange={e => setDescricao(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && canAdvance && go(1)}
          autoFocus
        />
        {sugestoes.length > 0 && (
          <>
            <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.5px', margin: '0 0 6px' }}>
              Sugestões rápidas
            </p>
            <div className="wiz-sugestoes">
              {sugestoes.map(s => (
                <button 
                  key={s} 
                  className="wiz-sug-pill" 
                  onClick={() => { 
                    setDescricao(s); 
                    setTimeout(() => go(1), 150); 
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );

    // Step 4 (receber only) — Cliente
    if (step === 4 && mode === 'receber') return (
      <div key="s4" className={cls}>
        <p className="wiz-step-label">Passo 5 de {stepLabels.length}</p>
        <h2 className="wiz-step-title">Quem vai pagar?</h2>
        <p className="wiz-step-sub">Nome do cliente ou pagador. <strong>Opcional</strong> — você pode deixar em branco.</p>
        <input
          className="wiz-cliente-input"
          type="text"
          placeholder="Ex: João da Silva, Empresa XYZ…"
          value={clienteNome}
          onChange={e => setClienteNome(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && go(1)}
          autoFocus
        />
        <span className="wiz-skip" onClick={() => go(1)}>Pular esta etapa →</span>
      </div>
    );

    // Confirmação
    return (
      <div key="sc" className={cls}>
        <p className="wiz-step-label">Revisão final</p>
        <h2 className="wiz-step-title">Tudo certo?</h2>
        <p className="wiz-step-sub">Confira o resumo antes de confirmar.</p>
        <div className="wiz-confirm-card">
          <p className="wiz-confirm-valor">{fmtCurrency(parseFloat(valor))}</p>
          {[
            { label: 'Tipo',        val: `${categoria?.emoji} ${categoria?.label}` },
            { label: mode === 'pagar' ? 'Vencimento' : 'Data prevista', val: fmtDate(dataVencimento) },
            { label: 'Descrição',   val: descricao },
            ...(mode === 'receber' && clienteNome ? [{ label: 'Cliente', val: clienteNome }] : []),
          ].map(r => (
            <div key={r.label} className="wiz-confirm-row">
              <span className="wiz-confirm-label">{r.label}</span>
              <span className="wiz-confirm-val">{r.val}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (success) return (
    <div className="wiz-overlay" style={rootStyle}>
      <div className="wiz-card">
        <div className="wiz-success">
          <div className="wiz-success-ring" style={{ background: cfg.accentSoft }}>
            {mode === 'pagar' ? '✅' : '🎉'}
          </div>
          <h2 className="wiz-success-title">Lançamento feito!</h2>
          <p className="wiz-success-sub">
            {cfg.successMsg}<br />
            O registro já aparece na sua listagem.
          </p>
          <button
            className="wiz-success-close"
            style={{ background: cfg.gradient }}
            onClick={onClose}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );

  const isConfirm = (mode === 'receber' ? step === 4 : step === 3);
  // Determine the confirm step number properly
  const confirmStepNum = mode === 'receber' ? 4 : 3;
  const isOnConfirm = step === confirmStepNum;

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div className="wiz-overlay" style={rootStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="wiz-card">

        {/* Header */}
        <div className="wiz-header">
          <div className="wiz-header-left">
            <div className="wiz-header-emoji">{cfg.emoji}</div>
            <p className="wiz-header-title">{cfg.title}</p>
            <p className="wiz-header-sub">{cfg.sub}</p>
          </div>
          <button className="wiz-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Progress bar */}
        <div className="wiz-progress" style={{ margin: '14px 32px 0' }}>
          <div
            className="wiz-progress-fill"
            style={{
              width: `${((step) / confirmStepNum) * 100}%`,
              background: cfg.gradient,
            }}
          />
        </div>

        {/* Step dots */}
        <div style={{ display: 'flex', gap: 6, padding: '10px 32px 0', justifyContent: 'center' }}>
          {stepLabels.map((l, i) => (
            <div key={i} style={{
              height: 6, flex: 1, borderRadius: 999,
              background: step > i ? cfg.accent : step === i ? cfg.accent : '#e2e8f0',
              opacity: step > i ? 0.4 : 1,
              transition: 'background .3s',
            }} />
          ))}
        </div>

        {/* Step content */}
        <div className="wiz-step-wrap">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="wiz-footer">
          {step > 0 && (
            <button className="wiz-btn-back" onClick={() => go(-1)}>←</button>
          )}
          <button
            className="wiz-btn-next"
            style={{ background: canAdvance ? cfg.gradient : undefined }}
            disabled={!canAdvance || submitting}
            onClick={() => {
              if (isOnConfirm) handleSubmit();
              else go(1);
            }}
          >
            {submitting
              ? 'Salvando…'
              : isOnConfirm
              ? `Confirmar ${mode === 'pagar' ? 'Pagamento' : 'Recebimento'} ✓`
              : 'Continuar →'
            }
          </button>
        </div>

      </div>
    </div>
  );
}

// ─── Trigger button (FAB "?") ─────────────────────────────────────────────────
// Adicione em ContasAPagar.js e ContasAReceber.js:
//
//   import WizardLancamento, { WizardTriggerBtn } from './WizardLancamento';
//
//   const [wizardOpen, setWizardOpen] = useState(false);
//   ...
//   <WizardTriggerBtn mode="pagar" onClick={() => setWizardOpen(true)} />
//   {wizardOpen && (
//     <WizardLancamento mode="pagar" onClose={() => setWizardOpen(false)} onSave={reload} />
//   )}

export function WizardTriggerBtn({ mode = 'pagar', onClick }) {
  const cfg = T[mode];
  return (
    <button
      onClick={onClick}
      title={mode === 'pagar' ? 'Lançamento rápido guiado' : 'Lançamento rápido guiado'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '9px 16px',
        background: '#ffffff',
        color: cfg.accentText,
        border: `1.5px solid ${cfg.accent}`,
        borderRadius: 12,
        fontFamily: "'Sora', 'DM Sans', sans-serif",
        fontSize: '0.82rem', fontWeight: 700,
        cursor: 'pointer',
        transition: 'all .18s',
        boxShadow: `0 2px 8px color-mix(in srgb, ${cfg.accent} 15%, transparent)`,
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = cfg.accentSoft;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = '#ffffff';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <span style={{ fontSize: '1rem' }}>🪄</span>
      Lançamento guiado
    </button>
  );
}